import { Flags } from '@constants/flags';
import {
  type ConfigurableTablePage,
  FeatureFlagTypes,
  Pages,
} from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import type { FeatureFlags } from '@onevesthq/feature-flags/web';
import { BULK_REBALANCE_BASE_PATH } from '@ovComponents/4-module/bulkRebalance';
import type { GlobalStats } from '@providers/globalStatsHooks';
import type { ActiveOrganizationProps } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { isViewedOrganizationFeesDisabled } from '@utils/isViewedOrganizationFeesDisabled';
import {
  AccountBalance,
  AdminPanelSettings,
  AssignmentInd,
  Brush,
  Dashboard as DashboardIcon,
  DesktopMac,
  Extension,
  FactCheck,
  Home as HomeIcon,
  InsertChart,
  Insights,
  MonetizationOn,
  People,
  Person,
  PieChart,
  ReceiptLong,
  Settings,
  SsidChart,
  type SvgIconComponent,
  TableChart as TableChartIcon,
} from 'hephaestus/webIcons';

export const MENU_FLAGS = [
  { flag: Flags.advisor_contact_repcode, options: { default: false } },
  { flag: Flags.alert_banners, options: { default: false } },
  {
    flag: Flags.configurable_table_pages,
    options: { default: false },
  },
  { flag: Flags.form_mapper, options: { default: false } },
  {
    flag: Flags.portfolio_trading_prototype,
    options: { default: false },
  },
  {
    flag: Flags.recon_activities_dashboard,
    options: { default: false },
  },
  { flag: Flags.recon_flags_dashboard, options: { default: false } },
  { flag: Flags.recon_tolerance_config, options: { default: false } },
  { flag: Flags.repcode_access, options: { default: false } },
  { flag: Flags.siem_data_feed, options: { default: false } },
  { flag: Flags.text_variants, options: { default: false } },
  { flag: Flags.use_fee_enable_toggle, options: { default: false } },
  { flag: Flags.use_improved_sales_tax, options: { default: false } },
] as const;

export type SectionEnabledArg = {
  featureFlags: FeatureFlags<typeof MENU_FLAGS>;
  organizationFeatureFlags: Set<FeatureFlagTypes>;
  activeOrganization: Omit<ActiveOrganizationProps, 'availableFeatureFlags'>;
  permissions: Set<string>;
  accessiblePages: Set<string>;
};

export type AppSectionArg = SectionEnabledArg & {
  dashboards?: {
    id: string;
    translatedName?: TranslatedString;
  }[];
  homePageConfiguration?: { translatedName?: TranslatedString } | null;
  configurableTablePages?: ConfigurableTablePage[];
  renameOptimizerToBulkRebalance: boolean;
};

export type SectionCountArg = {
  stats: GlobalStats;
};

export type SubSection = {
  name: string | TranslatedString;
  path: string;
  analyticEvent?: string;
  beta?: boolean;
  enabled?: (arg: SectionEnabledArg) => boolean;
  getCount?: (arg: SectionCountArg) => number | undefined;
};

export type SectionItem = SubSection & {
  icon: SvgIconComponent | string;
};

export type SectionGroup = {
  name: string | TranslatedString;
  icon: SvgIconComponent | string;
  subSections: SubSection[];
  beta?: boolean;
  enabled?: (arg: SectionEnabledArg) => boolean;
};

export type Section = SectionItem | SectionGroup;

const menuMap: Section[] = [
  {
    name: 'menu.workflows',
    icon: Insights,
    path: '/workflows',
    enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Workflows),
  },
  {
    name: 'menu.clientManagement',
    icon: People,
    subSections: [
      {
        name: 'menu.households',
        analyticEvent: ovAnalyticsEvents.clientManagementHouseholdSelect,
        path: '/households',
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Households),
      },
      {
        name: 'menu.individuals',
        analyticEvent: ovAnalyticsEvents.clientManagementClientsSelect,
        path: '/clients',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.IndividualClients),
      },
      {
        name: 'menu.nonIndividualClients',
        analyticEvent:
          ovAnalyticsEvents.clientManagementNonIndividualClientsSelect,
        path: '/nonIndividualClients',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.NonIndividualClients),
      },
      {
        name: 'menu.accountsList',
        path: '/accounts',
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Accounts),
      },
      {
        name: 'menu.accountGroupsList',
        path: '/accountGroups',
        enabled: ({ organizationFeatureFlags, accessiblePages }) => {
          const isFeatureFlagEnabled = organizationFeatureFlags.has(
            FeatureFlagTypes.AccountGroups,
          );
          const hasAccess = accessiblePages.has(Pages.AccountGroups);
          return isFeatureFlagEnabled && hasAccess;
        },
      },
    ],
  },
  {
    name: 'menu.portfolioManagement',
    icon: PieChart,
    subSections: [
      {
        name: 'menu.buildModels',
        analyticEvent:
          ovAnalyticsEvents.portfolioManagementModelPortfoliosSelect,
        path: '/buildModels',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.BuildModels),
      },
      {
        name: 'menu.modelFamily',
        analyticEvent:
          ovAnalyticsEvents.portfolioManagementModelPortfoliosSelect,
        path: '/modelFamilies',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.PortfolioModelFamilies),
      },
      {
        name: 'menu.modelPortfolios',
        analyticEvent:
          ovAnalyticsEvents.portfolioManagementModelPortfoliosSelect,
        path: '/modelPortfolios',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.ModelPortfolios),
      },
      {
        name: 'menu.portfolioTemplates',
        path: '/portfolioTemplates',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.PortfolioTemplates),
      },
      {
        name: 'menu.productShelfPage',
        analyticEvent: ovAnalyticsEvents.portfolioManagementProductShelfSelect,
        path: '/productShelf',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.ProductShelf),
      },
      {
        name: 'menu.assetClassesPage',
        analyticEvent: ovAnalyticsEvents.portfolioManagementAssetClassesSelect,
        path: '/assetClasses',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.AssetClasses),
      },
      {
        name: 'menu.tradingSchedules',
        analyticEvent:
          ovAnalyticsEvents.portfolioManagementTradingSchedulesSelect,
        path: '/schedules',
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Schedules),
      },
    ],
  },
  {
    name: 'menu.trading',
    icon: SsidChart,
    subSections: [
      {
        name: 'menu.rebalanceDashboard',
        path: '/portfolio-trading-prototype-v2',
        enabled: ({ featureFlags }) =>
          featureFlags[Flags.portfolio_trading_prototype].enabled,
      },
      {
        name: 'menu.rebalancing',
        analyticEvent: ovAnalyticsEvents.tradingRebalancingSelect,
        path: '/rebalances',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.Rebalancing),
      },
      {
        name: 'menu.rebalanceReport',
        analyticEvent: ovAnalyticsEvents.tradingRebalanceReportSelect,
        path: '/rebalanceReport',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.RebalanceReport),
      },
      {
        name: 'menu.portfolioOptimizer',
        analyticEvent: ovAnalyticsEvents.tradingPortfolioOptimizerSelect,
        path: '/portfolioOptimizers',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.PortfolioOptimizer),
      },
      {
        name: 'menu.bulkTrader',
        analyticEvent: ovAnalyticsEvents.tradingBulkTraderSelect,
        path: '/bulkTrader',
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.BulkTrader),
      },
    ],
  },
  {
    name: 'menu.compliance',
    icon: FactCheck,
    subSections: [
      {
        name: 'menu.accountsReview',
        analyticEvent: ovAnalyticsEvents.complianceAccountsReviewSelect,
        path: '/accountsReview',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.AccountsReview),
        getCount: ({ stats }) => stats.subAccountsToReviewCount,
      },
      {
        name: 'menu.complianceReview',
        analyticEvent: ovAnalyticsEvents.complianceComplianceReviewSelect,
        path: '/complianceReview',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.ComplianceReview),
        getCount: ({ stats }) => stats.complianceReviewCount,
      },
      {
        name: 'menu.transactionMonitoringTool',
        analyticEvent: ovAnalyticsEvents.complianceSuspiciousTransfersSelect,
        path: '/transactionMonitoringTool',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.SuspiciousTransactions),
        getCount: ({ stats }) => stats.alertsCount,
      },
      {
        name: 'menu.transactions',
        analyticEvent: ovAnalyticsEvents.complianceAllTransactionsSelect,
        path: '/transactions',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.Transactions),
      },
      {
        name: 'menu.elderlyClients',
        analyticEvent: ovAnalyticsEvents.complianceElderlyClientsSelect,
        path: '/elderlyClients',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.ElderlyClients),
      },
      {
        name: 'menu.annualSuitabilityReview',
        analyticEvent: ovAnalyticsEvents.complianceAnnualProfileUpdateSelect,
        path: '/annualSuitabilityReview',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.AnnualSuitabilityReview),
        getCount: ({ stats }) => stats.annualSuitabilityReviewCount,
      },
      {
        name: 'menu.bankAccountsToReview',
        analyticEvent: ovAnalyticsEvents.complianceNewBankAccountApprovalSelect,
        path: '/BankAccountsToReview',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.BankAccountsToReview),
        getCount: ({ stats }) => stats.bankAccountAwaitingReviewCount,
      },
      {
        name: 'menu.institutionsToReview',
        analyticEvent: ovAnalyticsEvents.complianceInstitutionsApprovalSelect,
        path: '/institutionsToReview',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.InstitutionsToReview),
        getCount: ({ stats }) => stats.institutionAwaitingReviewCount,
      },
      {
        name: 'menu.reconciliation',
        analyticEvent: ovAnalyticsEvents.systemManagementReconciliationSelect,
        path: '/reconciliation',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.Reconciliation),
        getCount: ({ stats }) => stats.flagsCount,
      },
      {
        name: 'menu.statementsReview',
        analyticEvent: ovAnalyticsEvents.systemManagementStatementsReviewSelect,
        path: '/statementsReview',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.StatementsReview),
        getCount: ({ stats }) => stats.statementsToReviewCount,
      },
    ],
  },
  {
    name: 'menu.reconciliation',
    icon: ReceiptLong,
    subSections: [
      {
        name: 'menu.activities',
        analyticEvent: ovAnalyticsEvents.systemManagementReconciliationSelect,
        path: '/reconciliationActivities',
        enabled: ({ featureFlags, accessiblePages, permissions }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.recon_activities_dashboard].enabled;
          const hasAccess = accessiblePages.has(Pages.Reconciliation);
          const hasPermission = permissions.has(
            'read:custodian_activity_basic',
          );
          return isFeatureFlagEnabled && hasAccess && hasPermission;
        },
      },
      {
        name: 'menu.flags',
        analyticEvent: ovAnalyticsEvents.systemManagementReconciliationSelect,
        path: '/flags',
        enabled: ({ featureFlags, accessiblePages }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.recon_flags_dashboard].enabled;
          const hasAccess = accessiblePages.has(Pages.Reconciliation);
          return isFeatureFlagEnabled && hasAccess;
        },
        getCount: ({ stats }) => stats.flagsCount,
      },
    ],
  },
  {
    name: 'menu.reports',
    analyticEvent: ovAnalyticsEvents.reportsSelect,
    icon: InsertChart,
    path: '/reports',
    enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Reports),
  },
  {
    name: 'menu.feeAndBilling',
    icon: MonetizationOn,
    enabled: ({ featureFlags, activeOrganization }) =>
      !isViewedOrganizationFeesDisabled(
        featureFlags[Flags.use_fee_enable_toggle].enabled,
        activeOrganization,
      ),
    subSections: [
      {
        name: 'menu.billingManagement',
        analyticEvent: ovAnalyticsEvents.feesAndBillingIncurredFeeSelect,
        path: '/billingManagement',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.BillingManagement),
      },
      {
        name: 'menu.feeGrids',
        analyticEvent: ovAnalyticsEvents.feesAndBillingFeeGridSelect,
        path: '/feeGrids',
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.FeeGrids),
      },
      {
        name: 'menu.billingSchedules',
        analyticEvent: ovAnalyticsEvents.feesAndBillingBillingSchedulesSelect,
        path: '/billingSchedules',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.BillingSchedules),
      },
    ],
  },
  {
    name: 'menu.systemManagement',
    icon: Settings,
    subSections: [
      {
        name: 'menu.bulkImport',
        analyticEvent: ovAnalyticsEvents.systemManagementBulkImportSelect,
        path: '/bulkImport',
        enabled: ({ accessiblePages }) => accessiblePages.has(Pages.BulkImport),
      },
      {
        name: 'menu.newsAndInsights',
        path: '/newsAndInsights',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.NewsAndInsight),
      },
      {
        name: 'menu.devSettings',
        analyticEvent:
          ovAnalyticsEvents.systemManagementIntegrationSettingsSelect,
        path: '/devSettings',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.DeveloperSettings),
      },
      {
        name: 'menu.orgSettings',
        analyticEvent:
          ovAnalyticsEvents.systemManagementOrganizationSettingsSelect,
        path: '/orgSettings',
        enabled: ({ accessiblePages }) =>
          accessiblePages.has(Pages.OrganizationSettings),
      },
    ],
  },
  {
    name: 'menu.admin',
    icon: AdminPanelSettings,
    subSections: [
      {
        name: 'menu.advisorContact',
        path: '/advisorContact',
        enabled: ({ featureFlags, permissions }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.advisor_contact_repcode].enabled;
          const hasPermission = [
            'read:organization_settings',
            'read:organization_users',
            'read:rep_code',
          ].every((permission) => permissions.has(permission));
          return isFeatureFlagEnabled && hasPermission;
        },
      },
    ],
  },
];

// Commenting out all those that aren't completed yet

const settingsMenuMap: Section[] = [
  {
    name: 'menu.general',
    icon: Settings,
    path: '/settings/general',
    enabled: ({ permissions }) =>
      ['read:organizations'].some((permission) => permissions.has(permission)),
  },
  {
    name: 'menu.theme',
    icon: Brush,
    subSections: [
      {
        name: 'menu.designer',
        path: '/settings/designer',
        enabled: ({ permissions }) =>
          ['read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.textDesigner',
        path: '/settings/textDesigner',
        enabled: ({ featureFlags, permissions }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.text_variants].enabled;
          const hasPermission = ['read:organizations'].some((permission) =>
            permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
      {
        name: 'menu.authenticationTheme',
        path: '/settings/authenticationTheme',
        enabled: ({ permissions }) =>
          ['read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.errorTheme',
        path: '/settings/errorTheme',
        enabled: ({ permissions }) =>
          ['read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
    ],
  },
  {
    name: 'menu.roles',
    icon: AssignmentInd,
    subSections: [
      {
        name: 'menu.roleProfiles',
        path: '/settings/roleProfiles',
        enabled: ({ permissions }) =>
          ['read:role_profiles'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.roles',
        path: '/settings/roles',
        enabled: ({ permissions }) =>
          ['read:roles'].some((permission) => permissions.has(permission)),
      },
      {
        name: 'menu.roleSettings',
        path: '/settings/roleSettings',
        enabled: ({ permissions }) =>
          ['read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
    ],
  },
  {
    name: 'menu.accessUsers',
    icon: Person,
    subSections: [
      {
        name: 'menu.accessUsers',
        path: '/settings/accessUsers',
        enabled: ({ permissions }) =>
          ['read:organization_users'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.authentication',
        path: '/settings/authenticationSettings',
        enabled: ({ permissions }) =>
          ['write:advanced_organization_settings'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.accessSettings',
        path: '/settings/accessSettings',
        enabled: ({ permissions }) =>
          ['read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.repCodes',
        path: '/settings/repCodes',
        enabled: ({ featureFlags, permissions }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.repcode_access].enabled;
          const hasPermission = ['read:rep_code'].some((permission) =>
            permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
    ],
  },
  {
    name: 'menu.custodianAndAccounts',
    icon: AccountBalance,
    subSections: [
      {
        name: 'menu.accounts',
        path: '/settings/accounts',
        enabled: ({ permissions }) =>
          ['read:feature_flag', 'read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.taxRankings',
        path: '/settings/taxRankings',
        enabled: ({ permissions }) =>
          ['read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.accountSettings',
        path: '/settings/accountSettings',
        enabled: ({ permissions }) =>
          ['read:organizations', 'read:feature_flags'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.accountGroupSettings',
        path: '/settings/accountGroupSettings',
        enabled: ({ permissions }) =>
          ['read:organizations', 'read:feature_flags'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.custodianConnections',
        path: '/settings/custodianConnections',
        enabled: ({ permissions }) =>
          ['read:custodian_connection'].some((permission) =>
            permissions.has(permission),
          ),
      },
    ],
  },
  {
    name: 'menu.productExperience',
    icon: DesktopMac,
    subSections: [
      {
        name: 'menu.generalProductExperience',
        path: '/settings/productExperience',
        enabled: ({ permissions }) =>
          ['read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.dashboards',
        path: '/settings/dashboards',
        enabled: ({ permissions }) =>
          ['read:dashboards'].some((permission) => permissions.has(permission)),
      },
      {
        name: 'menu.localizationConfiguration',
        path: '/settings/localizationConfiguration',
        enabled: ({ permissions }) =>
          ['read:organizations', 'read:localizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.localization',
        path: '/settings/localizations',
        enabled: ({ permissions }) =>
          ['read:organizations', 'read:localizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.newsAndInsights',
        path: '/settings/newsAndInsights',
        enabled: ({ permissions }) =>
          ['read:news_and_insights'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.notifications',
        path: '/settings/notifications',
        enabled: ({ permissions }) =>
          ['read:notification_definition', 'read:notification_group'].some(
            (permission) => permissions.has(permission),
          ),
      },
      {
        name: 'menu.alertBanners',
        path: '/settings/alertBanners',
        enabled: ({ featureFlags, permissions }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.alert_banners].enabled;
          const hasPermission = ['read:alert_banners'].some((permission) =>
            permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
      {
        name: 'menu.forms',
        path: '/settings/forms',
        enabled: ({ permissions }) =>
          ['read:form_template'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.formDefinitions',
        path: '/settings/formDefinitions',
        enabled: ({ permissions }) =>
          [
            'write:form_definition',
            'write:advanced_organization_settings',
          ].some((permission) => permissions.has(permission)),
        beta: true,
      },
      {
        name: 'menu.formMapper',
        path: '/settings/formMapper',
        enabled: ({ featureFlags, permissions }) => {
          const isFeatureFlagEnabled = featureFlags[Flags.form_mapper].enabled;
          const hasPermission = [
            'write:form_definition',
            'write:advanced_organization_settings',
          ].some((permission) => permissions.has(permission));
          return isFeatureFlagEnabled && hasPermission;
        },
        beta: true,
      },
      {
        name: 'menu.clientReports',
        path: '/settings/clientReports',
        enabled: ({ organizationFeatureFlags, permissions }) => {
          const isFeatureFlagEnabled = organizationFeatureFlags.has(
            FeatureFlagTypes.ConfigurableClientReports,
          );
          const hasPermission = ['read:organizations'].some((permission) =>
            permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
      {
        name: 'menu.statementsAndTaxDocuments',
        path: '/settings/statementsAndTaxDocuments',
        enabled: ({ permissions }) =>
          ['read:feature_flags'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.customFields',
        path: '/settings/customFields',
        enabled: ({ permissions }) =>
          ['read:custom_fields'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.pageConfigurations',
        path: '/settings/pageConfigurations',
        enabled: ({ permissions }) =>
          ['read:page_configurations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.customAttributeLabels',
        path: '/settings/customAttributeLabels',
        enabled: ({ organizationFeatureFlags, permissions }) => {
          const isFeatureFlagEnabled = organizationFeatureFlags.has(
            FeatureFlagTypes.TranslatedAttributes,
          );
          const hasPermission = ['write:advanced_organization_settings'].some(
            (permission) => permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
      {
        name: 'menu.homePageConfigurations',
        path: '/settings/homePageConfigurations',
        enabled: ({ organizationFeatureFlags, permissions }) => {
          const isFeatureFlagEnabled = organizationFeatureFlags.has(
            FeatureFlagTypes.ConfigurableHomePages,
          );
          const hasPermission = ['read:page_configurations'].some(
            (permission) => permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
      {
        name: 'menu.staticPageWidgetConfigurations',
        path: '/settings/staticPageWidgetConfigurations',
        enabled: ({ permissions }) =>
          ['read:page_configurations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.configurableTablePages',
        path: '/settings/configurableTablePages',
        enabled: ({ featureFlags, permissions }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.configurable_table_pages].enabled;
          const hasPermission = ['read:configurable_table_page'].some(
            (permission) => permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
      {
        name: 'menu.workflows',
        path: '/settings/workflows',
        enabled: ({ permissions }) =>
          ['read:workflow'].some((permission) => permissions.has(permission)),
      },
      {
        name: 'menu.featureFlags',
        path: '/settings/featureFlags',
        enabled: ({ permissions }) =>
          ['read:feature_flags'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.downtimeSchedule',
        path: '/settings/downtimeSchedule',
        enabled: ({ permissions }) =>
          ['read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
    ],
  },
  {
    name: 'menu.reconciliation',
    icon: ReceiptLong,
    subSections: [
      {
        name: 'menu.flagPolicies',
        path: '/settings/flagPolicies',
        enabled: ({ featureFlags, permissions }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.recon_flags_dashboard].enabled;
          const hasPermission = ['read:flags'].some((permission) =>
            permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
      {
        name: 'menu.reconTolerance',
        path: '/settings/reconTolerance',
        enabled: ({ featureFlags, permissions }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.recon_tolerance_config].enabled;
          const hasPermission = ['read:transaction', 'read:organizations'].some(
            (permission) => permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
    ],
  },
  {
    name: 'menu.portfolioManagements',
    icon: SsidChart,
    subSections: [
      {
        name: 'menu.transfers',
        path: '/settings/transfers',
        enabled: ({ permissions }) =>
          ['read:organizations', 'read:feature_flags'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.trades',
        path: '/settings/trades',
        enabled: ({ permissions }) =>
          ['read:organizations', 'read:feature_flags'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.tradingSchedules',
        analyticEvent:
          ovAnalyticsEvents.portfolioManagementTradingSchedulesSelect,
        path: '/settings/tradingSchedules',
        enabled: ({ permissions }) =>
          ['read:schedules'].some((permission) => permissions.has(permission)),
      },
      {
        name: 'menu.assetClasses',
        path: '/settings/assetClasses',
        enabled: ({ permissions }) =>
          ['read:asset_classes'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.compliance',
        path: '/settings/compliance',
        enabled: ({ permissions }) =>
          ['read:organizations', 'read:feature_flags'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.portfolioManagementSettings',
        path: '/settings/portfolioManagementSettings',
        enabled: ({ permissions }) =>
          ['write:organizations', 'write:advanced_organization_settings'].some(
            (permission) => permissions.has(permission),
          ),
      },
    ],
  },
  {
    name: 'menu.feesAndBilling',
    icon: MonetizationOn,
    subSections: [
      {
        name: 'menu.feeSettings',
        path: '/settings/feeSettings',
        enabled: ({ featureFlags, permissions }) => {
          const required = featureFlags[Flags.use_fee_enable_toggle].enabled
            ? ['read:organizations', 'read:fee_setting']
            : ['read:organizations'];
          return required.some((permission) => permissions.has(permission));
        },
      },
      {
        name: 'menu.feeGrids',
        analyticEvent: ovAnalyticsEvents.feesAndBillingFeeGridSelect,
        path: '/settings/feeGrids',
        enabled: ({ featureFlags, permissions }) => {
          const required = featureFlags[Flags.use_fee_enable_toggle].enabled
            ? ['read:fee_tier', 'read:organizations', 'read:fee_setting']
            : ['read:fee_tier', 'read:organizations'];
          return required.some((permission) => permissions.has(permission));
        },
      },
      {
        name: 'menu.billingSchedules',
        analyticEvent: ovAnalyticsEvents.feesAndBillingBillingSchedulesSelect,
        path: '/settings/billingSchedules',
        enabled: ({ featureFlags, permissions }) => {
          const required = featureFlags[Flags.use_fee_enable_toggle].enabled
            ? [
                'read:billing_schedule',
                'read:organizations',
                'read:fee_setting',
              ]
            : ['read:billing_schedule', 'read:organizations'];
          return required.some((permission) => permissions.has(permission));
        },
      },
      {
        name: 'menu.salesTax',
        path: '/settings/salesTax',
        enabled: ({ featureFlags, permissions }) =>
          featureFlags[Flags.use_improved_sales_tax].enabled &&
          permissions.has('read:sales_tax_configuration'),
      },
    ],
  },
  {
    name: 'menu.integrations',
    icon: Extension,
    subSections: [
      {
        name: 'menu.integrations',
        path: '/settings/integrations',
        enabled: ({ permissions }) =>
          ['read:integrations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.bulkImport',
        analyticEvent: ovAnalyticsEvents.systemManagementBulkImportSelect,
        path: '/settings/bulkImport',
        enabled: ({ permissions }) =>
          ['read:bulk_import'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.embedded',
        path: '/settings/embedded',
        enabled: ({ permissions }) =>
          ['read:organizations'].some((permission) =>
            permissions.has(permission),
          ),
      },
      {
        name: 'menu.apiTokens',
        path: '/settings/apiTokens',
        enabled: ({ permissions }) =>
          ['read:api_tokens'].some((permission) => permissions.has(permission)),
      },
      {
        name: 'menu.webhooks',
        path: '/settings/webhooks',
        enabled: ({ permissions }) =>
          ['read:webhook'].some((permission) => permissions.has(permission)),
      },
      {
        name: 'menu.siemDataFeed',
        path: '/settings/siemDataFeed',
        enabled: ({ featureFlags, permissions }) => {
          const isFeatureFlagEnabled =
            featureFlags[Flags.siem_data_feed].enabled;
          const hasPermission = ['read:siem_feeds'].some((permission) =>
            permissions.has(permission),
          );
          return isFeatureFlagEnabled && hasPermission;
        },
      },
    ],
  },
];

export const filterEnabledSections = (
  sections: Section[],
  arg: SectionEnabledArg,
): Section[] =>
  sections.flatMap((section): Section[] => {
    const isEnabled = (item: {
      enabled?: (arg: SectionEnabledArg) => boolean;
    }) => item.enabled?.(arg) !== false;

    if (!('subSections' in section)) {
      return isEnabled(section) ? [section] : [];
    }
    if (section.subSections.length === 0 || !isEnabled(section)) {
      return [];
    }
    const subSections = section.subSections.filter(isEnabled);
    if (subSections.length === 0) {
      return [];
    }
    return [{ ...section, subSections }];
  });

export const getSettingMenuMap = (arg: SectionEnabledArg) =>
  filterEnabledSections(settingsMenuMap, arg);

const toSectionName = (
  name:
    | ConfigurableTablePage['name']
    | ConfigurableTablePage['section']['name'],
): SubSection['name'] => name as SubSection['name'];

const configurableTablePageEnabled = ({
  featureFlags,
  permissions,
}: SectionEnabledArg) => {
  const isFeatureFlagEnabled =
    featureFlags[Flags.configurable_table_pages].enabled;
  const hasPermission = [
    'read:configurable_table_page',
    'read:configurable_table_data',
  ].some((permission) => permissions.has(permission));
  return isFeatureFlagEnabled && hasPermission;
};

const buildConfigurableTablePagesSections = (
  translateBackendString: (translatedObject: unknown) => string,
  pages: ConfigurableTablePage[],
): Section[] => {
  if (pages.length === 0) {
    return [];
  }
  const groupedPages = pages.reduce<
    Record<
      string,
      {
        sectionName: SubSection['name'];
        icon: Section['icon'];
        pages: SubSection[];
      }
    >
  >((acc, page) => {
    const sectionName = page.section.name;
    const sectionTranslatedName = translateBackendString(sectionName);
    if (!acc[sectionTranslatedName]) {
      acc[sectionTranslatedName] = {
        sectionName: toSectionName(sectionName),
        icon: page.section.icon || TableChartIcon,
        pages: [],
      };
    }
    acc[sectionTranslatedName].pages.push({
      name: toSectionName(page.name),
      path: `/configurableTablePage/${page.id}`,
      enabled: configurableTablePageEnabled,
    });
    return acc;
  }, {});

  return Object.values(groupedPages).map((group): Section => {
    if (group.pages.length === 1) {
      return {
        name: group.sectionName,
        icon: group.icon,
        path: group.pages[0].path,
        enabled: group.pages[0].enabled,
      };
    }
    return {
      name: group.sectionName,
      icon: group.icon,
      subSections: group.pages,
    };
  });
};

const dashboardAnalyticEvent = (name: string) => {
  if (name === 'Organization') {
    return ovAnalyticsEvents.dashboardOrganizationSelect;
  }
  if (name === 'Basic') {
    return ovAnalyticsEvents.dashboardBasicSelect;
  }
  if (name.startsWith('Portfolio Manager')) {
    return ovAnalyticsEvents.dashboardPortfolioManagerSelect;
  }
  if (name === 'Compliance') {
    return ovAnalyticsEvents.dashboardComplianceSelect;
  }
  if (name === 'Operations') {
    return ovAnalyticsEvents.dashboardOperationsSelect;
  }
};

const applyTradingSectionUpdates = (
  sections: Section[],
  renameOptimizerToBulkRebalance: boolean,
) => {
  if (!renameOptimizerToBulkRebalance) {
    return sections;
  }
  return sections.map((section) => {
    if (section.name !== 'menu.trading' || !('subSections' in section)) {
      return section;
    }
    return {
      ...section,
      subSections: section.subSections.map((subsec) => {
        if (subsec.name !== 'menu.portfolioOptimizer') {
          return subsec;
        }
        return {
          ...subsec,
          name: 'menu.bulkRebalance',
          path: BULK_REBALANCE_BASE_PATH,
          analyticEvent: ovAnalyticsEvents.tradingPortfolioOptimizerSelect,
        };
      }),
    };
  });
};

export const getMenuMap = (
  translateBackendString: (translatedObject: unknown) => string,
  {
    dashboards = [],
    homePageConfiguration,
    configurableTablePages = [],
    renameOptimizerToBulkRebalance,
    ...arg
  }: AppSectionArg,
) => {
  const hasHomePage = !!(
    homePageConfiguration &&
    arg.organizationFeatureFlags.has(FeatureFlagTypes.ConfigurableHomePages)
  );

  const dashboardItems: SubSection[] = dashboards.map((dashboard) => {
    const name =
      translateBackendString(dashboard.translatedName) ?? 'menu.dashboard';
    return {
      name,
      path: `/dashboard/${dashboard.id}`,
      analyticEvent: dashboardAnalyticEvent(name),
      enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Home),
    };
  });

  const dashboardsSection: Section =
    dashboardItems.length === 1
      ? {
          ...dashboardItems[0],
          icon: DashboardIcon,
        }
      : {
          name: 'menu.dashboards',
          icon: DashboardIcon,
          subSections: dashboardItems,
        };

  const homePageSection: Section = {
    name: homePageConfiguration?.translatedName ?? 'menu.homePage',
    icon: HomeIcon,
    path: '/',
    enabled: ({ accessiblePages }) => accessiblePages.has(Pages.Home),
  };

  const settingsSection: Section = {
    name: 'menu.settings',
    icon: Settings,
    beta: true,
    path: '/settings/general',
    enabled: ({ permissions }) =>
      ['write:advanced_organization_settings'].some((permission) =>
        permissions.has(permission),
      ),
  };

  return filterEnabledSections(
    [
      ...(hasHomePage ? [homePageSection] : []),
      dashboardsSection,
      ...applyTradingSectionUpdates(menuMap, renameOptimizerToBulkRebalance),
      ...buildConfigurableTablePagesSections(
        translateBackendString,
        configurableTablePages,
      ),
      settingsSection,
    ],
    arg,
  );
};
